/**
 * ChartGrid Component
 * Responsive grid for displaying analytics charts
 */

import React from 'react';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

export interface ChartGridProps {
  children: React.ReactNode;
  className?: string;
  minRowHeight?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 26rem), 1fr));
  gap: 1.25rem;
  width: 100%;
  height: 100%;

  /* Mobile tablet: Single column */
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  /* Small mobile: Reduced gap */
  @media (max-width: 479px) {
    gap: 0.75rem;
  }

  /* Extra small phones (≤360px) */
  @media (max-width: 360px) {
    gap: 0.5rem;
  }
`;

const ChartSlot = styled.div<{ $minHeight: number }>`
  display: flex;
  min-width: 0;
  min-height: ${({ $minHeight }) => `${$minHeight}px`};

  /* Allow charts to expand naturally based on content */
  > * {
    flex: 1;
    width: 100%;
  }

  /* Smooth transitions for height changes */
  transition: min-height 0.3s ease-in-out;

  /* Mobile: Adjusted min-heights for better fit */
  @media (max-width: 767px) {
    min-height: ${({ $minHeight }) => `${Math.max($minHeight - 30, 220)}px`};
  }

  @media (max-width: 479px) {
    min-height: ${({ $minHeight }) => `${Math.max($minHeight - 50, 200)}px`};
  }

  /* Extra small phones (≤360px) */
  @media (max-width: 360px) {
    min-height: ${({ $minHeight }) => `${Math.max($minHeight - 70, 180)}px`};
  }
`;

// ============================================================================
// Component
// ============================================================================

export const ChartGrid: React.FC<ChartGridProps> = ({
  children,
  className,
  minRowHeight = 280,
}) => {
  const childArray = React.Children.toArray(children);

  // Support dynamic number of charts (no longer limited to 6)
  // Filter out null/undefined children to avoid empty slots
  const validChildren = childArray.filter(
    (child) => child !== null && child !== undefined,
  );

  return (
    <GridContainer className={className}>
      {validChildren.map((child, index) => (
        <ChartSlot key={index} $minHeight={minRowHeight}>
          {child}
        </ChartSlot>
      ))}
    </GridContainer>
  );
};
